<template>
  <DxChart
    id="chart"
    :data-source="timelineIndexesFormatted"
    palette="Material"
    @legend-click="onLegendClick"
  >
    <DxCommonSeriesSettings
      type="line"
      argument-field="date"
    />
    <DxSeries
      value-field="mean"
      :name="index"
    />
    <DxSeries
      v-for="task in tasksSeriesFormatted"
      v-if="tasks.length > 0"
      :key="task.id"
      tag="constantLine"
      :name="task.name"
      :color="task.color"
    />
    <DxSeries
      v-for="observation in observationsSeriesFormatted"
      v-if="observations.length > 0"
      :key="observation.id"
      tag="constantLine"
      :name="observation.name"
      :color="observation.color"
    />
    <DxArgumentAxis
      argument-type="datetime"
      :value-margins-enabled="false"
      discrete-axis-division-mode="crossLabels"
    >
      <DxConstantLine
        v-for="(task, taskIndex) in filteredTasks"
        v-if="tasks.length > 0"
        :key="'task-' + taskIndex"
        :width="2"
        :value="task.init_date"
        :color="task.color"
        dash-style="dash"
      >
        <DxLabel text="" />
      </DxConstantLine>
      <DxConstantLine
        v-for="(observation, observationIndex) in filteredObservations"
        v-if="observations.length > 0"
        :key="'observation' + observationIndex"
        :width="2"
        :value="observation.date"
        :color="observation.color"
        dash-style="dash"
      >
        <DxLabel text="" />
      </DxConstantLine>
      <DxLabel
        display-mode="stagger"
        format="yyyy-MM-dd"
      />
    </DxArgumentAxis>
    <DxLegend
      vertical-alignment="bottom"
      horizontal-alignment="center"
      item-text-position="right"
    />
    <DxTitle :text="''" />
    <DxTooltip
      :enabled="true"
      :z-index="999999"
      :customize-tooltip="customizeTooltip"
    />
  </DxChart>
</template>
<script>
import {
  DxChart,
  DxSeries,
  DxArgumentAxis,
  DxCommonSeriesSettings,
  DxLegend,
  DxTitle,
  DxTooltip,
  DxConstantLine,
  DxLabel,
} from 'devextreme-vue/chart';
import moment from 'moment';
import Vue from 'vue';

export default {
  name: 'TimelineGraph',
  components: {
    DxChart,
    DxSeries,
    DxArgumentAxis,
    DxCommonSeriesSettings,
    DxLegend,
    DxTitle,
    DxTooltip,
    DxConstantLine,
    DxLabel,
  },
  props: {
    timelineIndexes: {
      type: Array,
      default: () => [],
    },
    index: {
      type: String,
      default: '',
    },
    tasks: {
      type: Array,
      default: () => [],
    },
    observations: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      hideConstantLine: {},
    };
  },
  computed: {
    tasksSeriesFormatted() {
      const tasksActivities = {};
      for (const task of this.tasks) {
        if (task.activity) {
          tasksActivities[task.activity] = {
            name: task.name,
            color: task.color,
          };
        }
      }

      return Object.values(tasksActivities).map(task => ({
        ...task,
        name: `Tarea - ${task.name}`,
      }));
    },
    observationsSeriesFormatted() {
      const observationsActivities = {};

      for (const observation of this.observations) {
        observationsActivities[observation.category] = {
          name: observation.category === 'Muestreo' 
            ? observation.analyse_type_name 
            : observation.subcategory,
          color: this.getColor(observation.category),
        };
      }

      return Object.values(observationsActivities).map(observation => ({
        ...observation,
        name: `Observacion - ${observation.name}`,
      }));
    },
    timelineIndexesFormatted() {
      const timelineIndexesFormatted = [];
      for (const timelineIndex of this.timelineIndexes) {
        if (timelineIndex.values.length !== 0) {
          const timeLineFiltered = this.applyTimeLinesFilterByIndex(timelineIndex.values);
          // GIS V2 puede no tener aún calculado el índice seleccionado para esta fecha
          // concreta (values no lo incluye) — se omite ese punto en vez de romper el gráfico.
          if (!timeLineFiltered) continue;
          timeLineFiltered.date = timelineIndex.date;
          timelineIndexesFormatted.push(timeLineFiltered);
        }
      }
      return timelineIndexesFormatted;
    },
    filteredTasks() {
      return this.tasks
        .map((task) => {
          const fullName = `Tarea - ${task.name}`;
          return {
            ...task,
            name: fullName,
          };
        })
        .filter((task) =>
          this.hideConstantLine[task.name] === undefined ||
          this.hideConstantLine[task.name] === false
        );
    },
    filteredObservations() {
      return this.observations
        .map((observation) => {
          const name = observation.category === 'Muestreo' 
            ? observation.analyse_type_name 
            : observation.subcategory;
          const fullName = `Observacion - ${name}`;

          return {
            ...observation,
            name: fullName,
            color: this.getColor(observation.category),
          };
        })
        .filter((observation) =>
          this.hideConstantLine[observation.name] === undefined ||
          this.hideConstantLine[observation.name] === false
        );
    }
  },
  methods: {
    getColor(category) {
      const key = category.trim().toLowerCase();
      const colorMap = {
        'incidencia': '#fbbf24',
        'recetario': '#4cd964',
        'muestreo': '#2dd4bf',
        'otros': '#db2777',
        'producción y calidad': '#f43f5e',
      };
      return colorMap[key] || '#000000';
    },
    customizeTooltip(pointInfo) {
      return {
        html: `<div><div class='tooltip-header'>${moment(pointInfo.argument).format('DD-MM-YYYY')}</div><div class='tooltip-body'>
          <span class='bold'>${this.$t('index')}: </span>${pointInfo.seriesName}
          </span> </div>
          <span class="bold"> ${this.$t('mean')}:</span> ${pointInfo.valueText}
        </span> </div></div></div>`,
      };
    },
    applyTimeLinesFilterByIndex(values) {
      let timeLine = null;
      values.forEach((value) => {
        if (value.index.toString().toUpperCase() === this.index.toString().toUpperCase()) {
          timeLine = value;
        }
      });

      return timeLine;
    },
    onLegendClick({ target: series }) {
      if (series.isVisible()) {
        series.hide();

        if (series.tag === 'constantLine') {
          Vue.set(this.hideConstantLine, series.name, true);
        }
      } else {
        series.show();

        if (series.tag === 'constantLine') {
          this.hideConstantLine[series.name] = false;
        }
      }

      this.$forceUpdate();
    },
  },
};
</script>

<style lang="scss" scoped>
@import './TimelineGraph.styles.scss';
</style>
